import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { useCart } from '../../cart/contexts/CartContext'
import { useScrapPreferenceModal } from '../../../contexts/ScrapPreferenceContext'
import { Battery } from '../../../shared/types/battery'
import { buildImageUrl } from '../../../config'

const CSS_HANDLES = [
  'batteryCard',
  'cardContainer',
  'cardImage',
  'cardImageContainer',
  'cardContent',
  'cardHeader',
  'cardTitle',
  'cardBrand',
  'cardModel',
  'cardSpecs',
  'cardSpec',
  'cardPrice',
  'cardOriginalPrice',
  'cardDiscount',
  'cardRating',
  'cardStars',
  'cardReviewCount',
  'cardStock',
  'cardActions',
  'cardButton',
  'cardButtonPrimary',
  'cardButtonSecondary'
] as const

interface BatteryCardProps {
  battery: Battery
  onAddToCart?: (batteryId: string) => void
}

const BatteryCard: React.FC<BatteryCardProps> = ({
  battery,
  onAddToCart
}) => {
  const handles = useCssHandles(CSS_HANDLES)
  const { addItem } = useCart()
  const scrapModal = useScrapPreferenceModal()
  const navigate = useNavigate()
  const [imageError, setImageError] = React.useState(false)

  const handleAddToCart = () => {
    if (scrapModal?.addItemWithScrapModal) {
      scrapModal.addItemWithScrapModal(battery, 1, true)
    } else {
      addItem(battery)
    }
    if (onAddToCart) {
      onAddToCart(battery.id)
    }
  }

  const handleViewDetails = () => {
    navigate(`/produto/${battery.slug}`)
  }

  const handleImageError = () => {
    setImageError(true)
  }

  // Reset erro de imagem quando a bateria mudar
  React.useEffect(() => {
    setImageError(false)
  }, [battery.id])

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const renderStars = (rating: number) => {
    const stars = []
    const fullStars = Math.floor(rating)
    const hasHalfStar = rating % 1 !== 0

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )
    }

    if (hasHalfStar) {
      stars.push(
        <svg key="half" className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
          <defs>
            <linearGradient id="half-star">
              <stop offset="50%" stopColor="currentColor" />
              <stop offset="50%" stopColor="transparent" />
            </linearGradient>
          </defs>
          <path fill="url(#half-star)" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )
    }

    const emptyStars = 5 - Math.ceil(rating)
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <svg key={`empty-${i}`} className="w-4 h-4 text-gray-300 fill-current" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )
    }

    return stars
  }

  return (
    <div className={`${handles.batteryCard} bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 overflow-hidden flex flex-col h-full`}>
      <div className={`${handles.cardContainer} relative flex flex-col h-full`}>
        {/* Badge de Desconto */}
        {battery.discount && (
          <div className="absolute top-4 left-4 z-10">
            <span className={`${handles.cardDiscount} bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg`}>
              -{battery.discount}% OFF
            </span>
          </div>
        )}

        {/* Imagem do Produto */}
        <div className={`${handles.cardImageContainer} relative overflow-hidden bg-gray-50`}>
          <Link to={`/produto/${battery.slug}`}>
            {!imageError && battery.images && battery.images.length > 0 ? (
              <img
                src={buildImageUrl(battery.images[0]) as string
                }
                alt={battery.name}
                onError={handleImageError}
                className={`${handles.cardImage} w-full h-56 object-contain transition-transform duration-300 hover:scale-105`}
              />
            ) : (
              <div className="w-full h-56 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <svg
                  className="w-24 h-24 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>
            )}
          </Link>

          {/* Overlay de Ações Rápidas */}
          <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center opacity-0 hover:opacity-100">
            <button
              onClick={handleViewDetails}
              className="bg-white text-gray-800 p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
              title="Ver detalhes"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Conteúdo do Card */}
        <div className={`${handles.cardContent} p-6 flex flex-col flex-1`}>
          {/* Header */}
          <div className={`${handles.cardHeader} mb-4`}>
            {battery.brand && (
              <div className={`${handles.cardBrand} text-sm font-semibold text-blue-600 mb-1`}>
                {battery.brand}
              </div>
            )}
            <h3 className={`${handles.cardTitle} text-lg font-bold text-gray-900 mb-2 line-clamp-2`}>
              <Link to={`/produto/${battery.slug}`} className="hover:text-blue-600 transition-colors">
                {battery.name}
              </Link>
            </h3>
            {battery.model && (
              <div className={`${handles.cardModel} text-sm text-gray-500`}>
                Modelo: {battery.model}
              </div>
            )}
          </div>

          {/* Especificações Principais */}
          <div className={`${handles.cardSpecs} mb-4`}>
            {(battery.capacity != null && battery.capacity > 0) || (battery.voltage != null && battery.voltage > 0) ? (
              <div className={`grid gap-3 ${(battery.capacity != null && battery.capacity > 0) && (battery.voltage != null && battery.voltage > 0) ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {battery.capacity != null && battery.capacity > 0 && (
                  <div className={`${handles.cardSpec} bg-gray-50 rounded-lg p-3 text-center`}>
                    <div className="text-xs text-gray-600 mb-1">Capacidade</div>
                    <div className="font-bold text-gray-900">{battery.capacity}Ah</div>
                  </div>
                )}
                {battery.voltage != null && battery.voltage > 0 && (
                  <div className={`${handles.cardSpec} bg-gray-50 rounded-lg p-3 text-center`}>
                    <div className="text-xs text-gray-600 mb-1">Voltagem</div>
                    <div className="font-bold text-gray-900">{battery.voltage}V</div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Avaliação */}
          <div className={`${handles.cardRating} flex items-center justify-between mb-4`}>
            <div className="flex items-center">
              <div className={`${handles.cardStars} flex mr-2`}>
                {renderStars(battery.rating)}
              </div>
              <span className={`${handles.cardReviewCount} text-sm text-gray-600`}>
                ({battery.reviewCount})
              </span>
            </div>
            <div className="text-sm text-gray-500">
              {battery.weight}kg
            </div>
          </div>

          {/* Preço */}
          <div className={`${handles.cardPrice} mb-4`}>
            {battery.originalPrice && battery.originalPrice > battery.price && (
              <div className={`${handles.cardOriginalPrice} text-sm text-gray-500 line-through mb-1`}>
                {formatPrice(battery.originalPrice)}
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-blue-600">
                {formatPrice(battery.price)}
              </span>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-700">
                  Ou 12x de {formatPrice(battery.price / 12)}
                </div>
              </div>
            </div>
          </div>

          {/* Estoque */}
          <div className={`${handles.cardStock} mb-4`}>
            {battery.stock > 0 ? (
              <div className="flex items-center text-sm text-green-600 font-medium">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Em estoque ({battery.stock} unidades)
              </div>
            ) : (
              <div className="flex items-center text-sm text-red-600 font-medium">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                Indisponível
              </div>
            )}
          </div>

          {/* Ações */}
          <div className={`${handles.cardActions} mt-auto gap-3 flex flex-col`}>
            <button
              onClick={handleAddToCart}
              disabled={battery.stock === 0}
              className={`${handles.cardButton} ${handles.cardButtonPrimary} w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl`}
            >
              {battery.stock > 0 ? 'Adicionar ao Carrinho' : 'Indisponível'}
            </button>
            <button
              onClick={handleViewDetails}
              className={`${handles.cardButton} ${handles.cardButtonSecondary} w-full px-4 py-3 border-2 border-blue-600 text-blue-600 rounded-xl hover:bg-blue-50 transition-colors font-semibold`}
            >
              Ver Detalhes
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BatteryCard

